/**
 * Lightbox — view a photo LARGE while selecting (round 3). Opened from a photo
 * tile's magnifier or by double-click. ‹ › arrows (buttons + keyboard) step
 * through the photos in the grid's CURRENT sort order, the caption shows the
 * filename + megapixels, and a Select/Deselect toggle edits the selection
 * without leaving the lightbox. Round 4: the stage is CONSTRAINED to the
 * viewport (~85vw / ~80vh, object-fit contain) and an "Order print" button
 * closes the lightbox into the per-image print editor. Escape closes
 * (ModalShell owns the stack). Cheap by design: reuses the preview object
 * URLs, no new dependencies.
 */

import { useEffect } from 'react';
import { formatMpDims } from '../../lib/format';
import type { LocalPhoto } from '../../lib/types';
import { ModalShell } from './ModalShell';

export interface LightboxProps {
  /** Photos in the grid's current sort order (ready only). */
  photos: LocalPhoto[];
  photoId: string;
  selection: ReadonlySet<string>;
  onNavigate: (photoId: string) => void;
  onToggleSelect: (photoId: string) => void;
  /** Close the lightbox and open the "add a print" editor for this photo. */
  onOrderPrint: (photoId: string) => void;
  onClose: () => void;
}

export function Lightbox({
  photos,
  photoId,
  selection,
  onNavigate,
  onToggleSelect,
  onOrderPrint,
  onClose,
}: LightboxProps) {
  const index = photos.findIndex((p) => p.id === photoId);
  const photo = index >= 0 ? photos[index] : undefined;
  const prev = index > 0 ? photos[index - 1] : undefined;
  const next = index >= 0 && index < photos.length - 1 ? photos[index + 1] : undefined;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft' && prev) onNavigate(prev.id);
      else if (e.key === 'ArrowRight' && next) onNavigate(next.id);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [prev, next, onNavigate]);

  if (!photo) return null;
  const selected = selection.has(photo.id);

  return (
    <ModalShell
      onClose={onClose}
      width="min(1100px, 85vw)"
      label={`View large · ${photo.filename}`}
      className="lightbox"
    >
      <div className="lightbox__stage">
        {photo.previewUrl ? (
          <img className="lightbox__img" src={photo.previewUrl} alt={photo.filename} draggable={false} />
        ) : (
          <span className="lightbox__ph" />
        )}
        <button
          type="button"
          className="lightbox__nav lightbox__nav--prev"
          aria-label="Previous photo"
          disabled={!prev}
          onClick={() => prev && onNavigate(prev.id)}
        >
          ‹
        </button>
        <button
          type="button"
          className="lightbox__nav lightbox__nav--next"
          aria-label="Next photo"
          disabled={!next}
          onClick={() => next && onNavigate(next.id)}
        >
          ›
        </button>
        <button type="button" className="lightbox__close" aria-label="Close" onClick={onClose}>
          ✕
        </button>
      </div>
      <div className="lightbox__bar">
        <div className="lightbox__meta">
          <span className="lightbox__file" title={photo.filename}>
            {photo.filename}
          </span>
          <span className="lightbox__dims">
            {formatMpDims(photo.width, photo.height)} px · {index + 1} of {photos.length}
          </span>
        </div>
        <div className="lightbox__actions">
          <button
            type="button"
            className={`btn btn--secondary lightbox__select`}
            aria-pressed={selected}
            onClick={() => onToggleSelect(photo.id)}
          >
            {selected ? '✓ Selected · deselect' : 'Select this photo'}
          </button>
          <button
            type="button"
            className="btn btn--gold lightbox__order"
            onClick={() => onOrderPrint(photo.id)}
          >
            Order print
          </button>
        </div>
      </div>
    </ModalShell>
  );
}
